import React, { useId } from 'react';
import { X } from 'lucide-react';
import { cx } from './classNames';
import { Portal } from './Portal';
import { useOverlayBehavior } from './overlayBehavior';
import './ui-overlay.css';

export type DrawerPlacement = 'right' | 'left';
export type DrawerSize = 'sm' | 'md' | 'lg';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** 出てくる辺。既定は右（詳細・編集）。スマホのナビは左を使う */
  placement?: DrawerPlacement;
  size?: DrawerSize;
  footer?: React.ReactNode;
  dismissOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  closeLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

export function Drawer({
  open,
  onClose,
  title,
  placement = 'right',
  size = 'md',
  footer,
  dismissOnBackdrop = true,
  closeOnEscape = true,
  closeLabel = '閉じる',
  className,
  children,
}: DrawerProps) {
  const containerRef = useOverlayBehavior({ open, onClose, closeOnEscape });
  const autoId = useId();
  const titleId = `ui-drawer-title-${autoId}`;

  if (!open) return null;

  return (
    <Portal>
      <div
        className={cx('ui-overlay', `ui-overlay--${placement}`)}
        data-testid="ui-drawer-overlay"
        onMouseDown={(event) => {
          if (dismissOnBackdrop && event.target === event.currentTarget) onClose();
        }}
      >
        <aside
          ref={containerRef}
          className={cx('ui-drawer', `ui-drawer--${placement}`, `ui-drawer--${size}`, className)}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          data-testid="ui-drawer"
        >
          <header className="ui-drawer__header">
            <h2 className="ui-drawer__title" id={titleId}>
              {title}
            </h2>
            <button type="button" className="ui-overlay__close" onClick={onClose} aria-label={closeLabel}>
              <X aria-hidden="true" />
            </button>
          </header>

          <div className="ui-drawer__body">{children}</div>

          {footer != null && <footer className="ui-drawer__footer">{footer}</footer>}
        </aside>
      </div>
    </Portal>
  );
}
